import {
  SUMMARY_CAPABILITIES,
  type FeatureAvailabilityReason,
  type LayoutResponse,
  type ResolvedConfiguration,
  type ScopeViewMode,
  type SummaryCapability,
} from './types';

/**
 * UI-side permission checks, ported from portal-ui-lib's `AuthorizationService` and
 * `ConfigurationState.can`. These only decide what to show; the backend enforces every one of
 * them again on the request.
 *
 * Two kinds of key, as in Angular:
 * - the five summary booleans (`view`, `create`, …), read from the layout's user-level snapshot;
 * - a Permission string (`template:read`, `lead:update`), read from `GET /me/access`.
 *
 * Not Angular's source for Permission strings: Angular read them from the feature
 * configuration, whose list is the role's code baseline *without* the per-tenant and per-node
 * role overrides (backend `resolveUserCapabilities`). `/me/access` returns the effective set —
 * what `authorize()` on the backend enforces — so a granted override shows its button and a
 * revoked one hides it.
 *
 * Not ported: the capability-key validation against manifest `capabilityDefinition` blocks. It
 * validated the retired `feature.component.x.action` format, so it flagged every real
 * Permission check as invalid; the backend calls those blocks vestigial.
 */

export interface AuthorizationContext {
  /** ADMIN role at GLOBAL scope (from `/auth/whoami`). */
  isGlobalAdmin: boolean;
  viewMode: ScopeViewMode;
  /** The app's layout; undefined while it loads. */
  layout: LayoutResponse | undefined;
  /** Effective permissions from `/me/access`; undefined while they load. */
  permissions: readonly string[] | undefined;
}

export interface AuthorizationResult {
  allowed: boolean;
  /** The answer is not known yet: what it depends on is still loading. Render neither branch. */
  pending: boolean;
}

function isSummaryCapability(key: string): key is SummaryCapability {
  return (SUMMARY_CAPABILITIES as readonly string[]).includes(key);
}

/**
 * May the user do `key`?
 *
 * Differences from the Angular service, both deliberate:
 * - The GLOBAL-admin shortcut applies in `admin` view mode only. `scoped` is the admin's
 *   preview of a node as its users see it, so the answer comes from the same sources as anyone
 *   else's. (The admin's own permissions are still the full set, server-side too: route
 *   authorization ignores `viewMode`; only configuration and layout resolution honour it.)
 * - `pending` is true whenever the source the answer comes from has not loaded. Angular
 *   answered a summary check with `{ allowed: false, pending: false }` during layout load, which
 *   flashes the "not allowed" branch on every page load.
 */
export function authorize(context: AuthorizationContext, key: string): AuthorizationResult {
  if (context.isGlobalAdmin && context.viewMode !== 'scoped') {
    return { allowed: true, pending: false };
  }

  if (isSummaryCapability(key)) {
    if (!context.layout) {
      return { allowed: false, pending: true };
    }
    return { allowed: context.layout.capabilities[key] === true, pending: false };
  }

  if (!context.permissions) {
    return { allowed: false, pending: true };
  }
  return { allowed: context.permissions.includes(key), pending: false };
}

/** All of `keys`; pending if any answer is. */
export function authorizeAll(
  context: AuthorizationContext,
  keys: readonly string[],
): AuthorizationResult {
  const results = keys.map(key => authorize(context, key));
  return {
    allowed: results.every(result => result.allowed),
    pending: results.some(result => result.pending),
  };
}

export type FeatureAvailability =
  | { available: true }
  | {
      available: false;
      /** `unknown`: the response did not say. */
      reason: Exclude<FeatureAvailabilityReason, 'available'> | 'unknown';
    };

/**
 * Has the tenant bought this feature, and has no org node on the path switched it off?
 *
 * Returns the reason, not a message: the Angular helper returned hardcoded English sentences,
 * which no screen may show (user-visible text goes through the feature schema's translations).
 *
 * Fails closed, as Angular did: the backend sets `featureAvailability` on every resolution
 * (configuration-resolution.service.ts), so its absence means a malformed answer, not a free pass.
 */
export function featureAvailability(configuration: ResolvedConfiguration): FeatureAvailability {
  const availability = configuration.meta.featureAvailability;
  if (!availability) {
    return { available: false, reason: 'unknown' };
  }
  if (availability.available) {
    return { available: true };
  }
  return {
    available: false,
    reason: availability.reason === 'tenant_disabled' ? 'tenant_disabled' : 'orgnode_disabled',
  };
}
